import { Injectable } from '@angular/core';
import { select } from '@ngrx/store';
import { CmsActions, CmsService, ContentSlotData, Page, PageContext, StateUtils } from '@spartacus/core';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import {
  getCurrentSlotSelectorFactory,
  getPageData,
  getPageStateIndexLoaderState,
  getPageStateIndexValue,
} from '../store/selectors/page.selectors';

@Injectable({
  providedIn: 'root',
})
export class CustomCmsService extends CmsService {
  getCurrentPage(): Observable<Page> {
    return this.routingService
      .getPageContext()
      .pipe(switchMap((pageContext) => this.store.select(getPageData(pageContext))));
  }

  getPageState(pageContext: PageContext): Observable<Page> {
    return this.store.pipe(select(getPageData(pageContext)));
  }

  /**
   * Given pageContext, return whether the CMS page data exists or not
   * @param pageContext
   */
  hasPage(pageContext: PageContext, forceReload = false): Observable<boolean> {
    return this.store.pipe(
      select(getPageStateIndexLoaderState(pageContext)),
      tap((entity: StateUtils.LoaderState<string>) => {
        const attemptedLoad = entity.loading || entity.success || entity.error;
        const shouldReload = forceReload && !entity.loading;
        if (!attemptedLoad || shouldReload) {
          this.store.dispatch(new CmsActions.LoadCmsPageData(pageContext));
          forceReload = false;
        }
      }),
      filter((entity) => {
        if (!Object.prototype.hasOwnProperty.call(entity, 'value')) {
          // if we have incomplete state from SSR failed load transfer state,
          // we should wait for reload and actual value
          return false;
        }

        return Boolean(entity.success || (entity.error && !entity.loading));
      }),
      map((entity) => Boolean(entity.success)),
      catchError(() => of(false)),
    );
  }

  getPageIndex(pageContext: PageContext): Observable<string> {
    return this.store.pipe(select(getPageStateIndexValue(pageContext)));
  }

  getContentSlot(position: string): Observable<ContentSlotData> {
    return this.routingService
      .getPageContext()
      .pipe(
        switchMap((pageContext) =>
          this.store.pipe(select(getCurrentSlotSelectorFactory(pageContext, position)), filter(Boolean)),
        ),
      );
  }
}
